import { useCallback, useEffect, useState } from "react";
import { isValidEmailAddress } from "@remo-co/email";
import { Box, ChipInput, Typography } from "..";

const separateEmailString = (val: string) => {
  const separator = val.includes(",") ? "," : " ";
  return val.split(separator) ?? [];
};

const checkInvalidEmails = (emails: string[]) =>
  emails.some((email) => !isValidEmailAddress(email));

interface Props {
  limit?: number;
  value: string[];
  onChange?: (emails: string[]) => void;
  validate?: (someInvalid: boolean) => void;
  placeholder: string;
  showLimit?: boolean;
  invalidEmailsMessage: string;
  getRemainingMessage: (remaining: string) => string;
  getExceededMessage: (remaining: string) => string;
}

const EmailsInput = ({
  value = [],
  onChange,
  validate,
  placeholder,
  limit = NaN,
  showLimit = true,
  invalidEmailsMessage,
  getRemainingMessage,
  getExceededMessage,
}: Props): JSX.Element => {
  const unlimited = Number.isNaN(limit);
  const [emails, setEmails] = useState<string[]>(value);

  const updateValue = useCallback(
    (updatedEmails: string[]) => {
      setEmails(updatedEmails);
      const limitExceeded = !unlimited && updatedEmails.length > limit;
      if (!limitExceeded) {
        onChange?.(updatedEmails);
        validate?.(checkInvalidEmails(updatedEmails));
      }
    },
    [unlimited, limit, onChange, validate],
  );

  const addEmails = (values: string[]) => {
    const cleanedValues = values
      .map((val) => val.trim().toLocaleLowerCase())
      .filter(Boolean);
    updateValue([...new Set([...emails, ...cleanedValues])]);
  };

  const handleBlur = (email: string) => {
    if (email) {
      addEmails(email?.split(","));
    }
  };

  const handleAddChip = (email: string) => {
    addEmails(separateEmailString(email));
  };

  const handleDeleteChip = (deletedEmail: string) => {
    updateValue(emails.filter((email) => deletedEmail !== email));
  };

  useEffect(() => {
    if (Array.isArray(value)) {
      updateValue(value);
    } else if (value) {
      setEmails(separateEmailString(value));
    }
  }, [value, updateValue]);

  const remaining = limit - (emails?.length ?? 0);
  const description =
    showLimit && remaining > 0 ? getRemainingMessage(`${remaining}`) : "";

  const errors = [];
  if (!unlimited && remaining < 0) {
    errors.push(getExceededMessage(`${Math.abs(remaining)}`));
  }
  if (checkInvalidEmails(emails)) {
    errors.push(invalidEmailsMessage);
  }

  const error =
    errors.length > 0 ? (
      <>
        {errors.map((err) => (
          <Typography display="block" color="error" component="span" key={err}>
            {err}
          </Typography>
        ))}
      </>
    ) : undefined;

  return (
    <Box width="100%" marginBottom={0.5}>
      <ChipInput
        fullWidth
        data={emails}
        validate={isValidEmailAddress}
        onAdd={(chip) => handleAddChip(chip)}
        onDelete={handleDeleteChip}
        onBlur={handleBlur}
        placeholder={placeholder}
        description={description}
        error={error}
      />
    </Box>
  );
};

export default EmailsInput;
